import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCopilot, AskAgentButton } from '../copilot';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  ClipboardText,
  ListNumbers,
  Hand,
  Trophy,
  MapPin,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  Warning,
  Info,
  CaretRight,
  User,
  Users,
  Upload,
  Download,
} from '@phosphor-icons/react';

export const QuickCanvassBidBoard = () => {
  const { api, user, isAdmin } = useAuth();
  const { openCopilot, setEntityContext } = useCopilot();
  const fileInputRef = useRef(null);
  
  // State
  const [bidOrder, setBidOrder] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [eligibility, setEligibility] = useState(null);
  const [loadingBidOrder, setLoadingBidOrder] = useState(true);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [loadingMyBids, setLoadingMyBids] = useState(true);
  const [bidDialogOpen, setBidDialogOpen] = useState(false);
  const [submittingBid, setSubmittingBid] = useState(false);
  
  // Overtime upload state
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchBidOrder();
    fetchJobs();
    fetchMyBids();
  }, []);

  const fetchBidOrder = async () => {
    setLoadingBidOrder(true);
    try {
      const response = await api.get('/canvass/bid-order');
      setBidOrder(response.data?.users || []);
    } catch (error) {
      toast.error('Failed to fetch bid order');
    } finally {
      setLoadingBidOrder(false);
    }
  };

  const fetchJobs = async () => {
    setLoadingJobs(true);
    try {
      const response = await api.get('/canvass/jobs');
      // Filter to only show open jobs
      const openJobs = (response.data || []).filter(j => j.status === 'open');
      setJobs(openJobs);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchMyBids = async () => {
    setLoadingMyBids(true);
    try {
      const response = await api.get('/canvass/my-bids');
      setMyBids(response.data || []);
    } catch (error) {
      console.error('Failed to fetch my bids:', error);
    } finally {
      setLoadingMyBids(false);
    }
  };

  const checkEligibility = async (jobId) => {
    try {
      const response = await api.get(`/canvass/jobs/${jobId}/eligibility`);
      setEligibility(response.data);
    } catch (error) {
      setEligibility({ is_eligible: false, reasons: ['Failed to check eligibility'], can_bid: false });
    }
  };

  const handleSelectJob = async (job) => {
    setSelectedJob(job);
    setEntityContext('job', job.id, job.title);
    await checkEligibility(job.id);
    setBidDialogOpen(true);
  };

  const handleSubmitBid = async () => {
    if (!selectedJob || !eligibility?.can_bid) return;
    
    setSubmittingBid(true);
    try {
      const response = await api.post('/canvass/bids', {
        job_id: selectedJob.id,
        overtime_hours_at_bid: eligibility.current_overtime_hours || 0
      });
      
      toast.success(`Bid submitted! You are ranked #${response.data.rank}`);
      setBidDialogOpen(false);
      setSelectedJob(null);
      setEligibility(null);
      
      // Refresh data
      fetchMyBids();
      fetchJobs();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit bid');
    } finally {
      setSubmittingBid(false);
    }
  };

  const getMyRank = () => {
    const myEntry = bidOrder.find(u => u.user_id === user?.id);
    return myEntry?.rank || '—';
  };

  const getMyOvertime = () => {
    const myEntry = bidOrder.find(u => u.user_id === user?.id);
    return myEntry?.overtime_hours?.toFixed(1) || '0.0';
  };

  const getOutcomeBadge = (outcome) => {
    switch (outcome) {
      case 'awarded':
        return <Badge className="bg-emerald-100 text-emerald-800"><Trophy size={12} className="mr-1" /> Awarded</Badge>;
      case 'not_selected':
        return <Badge variant="secondary"><XCircle size={12} className="mr-1" /> Not Selected</Badge>;
      default:
        return <Badge variant="outline"><Clock size={12} className="mr-1" /> Pending</Badge>;
    }
  };

  // Overtime Upload Functions
  const downloadOvertimeTemplate = () => {
    const csv = 'email,overtime_hours,last_overtime_date\njohn.doe@company.com,12.5,2026-01-15\njane.smith@company.com,8.0,2026-01-10';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'overtime_template.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Template downloaded');
  };

  const handleOvertimeFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    if (!file.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.trim().split('\n');
      const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
      
      if (!headers.includes('email') || !headers.includes('overtime_hours')) {
        toast.error('CSV must have "email" and "overtime_hours" columns');
        return;
      }
      
      const rows = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim());
        const row = {};
        headers.forEach((h, idx) => {
          row[h] = values[idx] || '';
        });
        rows.push(row);
      }
      
      setUploadPreview({ file, headers, rows: rows.slice(0, 10), totalRows: rows.length });
    };
    reader.readAsText(file);
  };

  const handleOvertimeUpload = async () => {
    if (!uploadPreview?.file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadPreview.file);
    if (uploadNotes) formData.append('notes', uploadNotes);
    
    try {
      const response = await api.post('/admin/overtime/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success(`Uploaded! ${response.data.rows_processed} rows processed`);
      if (response.data.rows_failed > 0) {
        toast.warning(`${response.data.rows_failed} rows failed`);
      }
      
      setUploadDialogOpen(false);
      setUploadPreview(null);
      setUploadNotes('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      
      // Refresh bid order
      fetchBidOrder();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="p-6 lg:p-10 animate-fade-in" data-testid="bid-board-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900 mb-2">
            Bid Board
          </h1>
          <p className="text-slate-600">
            View open jobs, your bid position, and place bids
          </p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && (
            <Button 
              variant="outline" 
              onClick={() => setUploadDialogOpen(true)}
              data-testid="upload-overtime-btn"
            >
              <Upload size={16} className="mr-2" />
              Upload Overtime
            </Button>
          )}
          <AskAgentButton
            module="Quick Canvass"
            prompts={[
              { label: 'Explain my ranking', prompt: 'How is my bid ranking determined and what affects my position?' },
              { label: 'Bidding rules', prompt: 'What are the rules for bidding on jobs?' },
            ]}
          />
        </div>
      </div>

      {/* My Status Card */}
      <Card className="mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <User size={20} className="text-blue-600" />
                <span className="text-slate-600">Your Position:</span>
                <span className="text-2xl font-bold text-blue-700">#{getMyRank()}</span>
              </div>
              <div className="h-8 w-px bg-blue-200" />
              <div className="flex items-center gap-2">
                <Clock size={20} className="text-blue-600" />
                <span className="text-slate-600">Overtime Hours:</span>
                <span className="text-2xl font-bold text-blue-700">{getMyOvertime()}</span>
              </div>
            </div>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="sm">
                    <Info size={16} />
                  </Button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs">
                  <p>Lower overtime hours = higher bid priority. Ties are broken by last overtime date (earlier = higher priority).</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardContent>
      </Card>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Panel - Bid Order */}
        <div className="lg:col-span-3">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ListNumbers size={20} />
                Bid Order
              </CardTitle>
              <CardDescription>Ranked by overtime (lowest first)</CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <ScrollArea className="h-[500px]">
                {loadingBidOrder ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : bidOrder.length === 0 ? (
                  <div className="text-center py-8 text-slate-500 text-sm">
                    No eligible users
                  </div>
                ) : (
                  <div className="divide-y">
                    {bidOrder.map((person) => (
                      <div
                        key={person.user_id}
                        className={`p-3 flex items-center gap-3 ${
                          person.user_id === user?.id ? 'bg-blue-50' : 'hover:bg-slate-50'
                        }`}
                        data-testid={`bid-order-${person.user_id}`}
                      >
                        <Badge
                          variant={person.user_id === user?.id ? 'default' : 'outline'}
                          className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                        >
                          {person.rank}
                        </Badge>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${
                            person.user_id === user?.id ? 'text-blue-700' : 'text-slate-900'
                          }`}>
                            {person.name}
                            {person.user_id === user?.id && ' (You)'}
                          </p>
                          <p className="text-xs text-slate-500">
                            {person.overtime_hours?.toFixed(1)}h OT
                            {person.classification && ` • ${person.classification}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Center Panel - Open Jobs */}
        <div className="lg:col-span-5">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardText size={20} />
                Open Jobs
              </CardTitle>
              <CardDescription>Jobs available for bidding</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingJobs ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <ClipboardText size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No open jobs at this time</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {jobs.map((job) => {
                    const alreadyBid = myBids.some(b => b.job_id === job.id);
                    return (
                      <div
                        key={job.id}
                        className={`p-4 border rounded-lg transition-all cursor-pointer ${
                          alreadyBid 
                            ? 'border-emerald-200 bg-emerald-50' 
                            : 'border-slate-200 hover:border-blue-300 hover:shadow-sm'
                        }`}
                        onClick={() => !alreadyBid && handleSelectJob(job)}
                        data-testid={`job-card-${job.id}`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-900 truncate">
                                {job.title}
                              </h4>
                              {alreadyBid && (
                                <Badge className="bg-emerald-100 text-emerald-700 text-xs">
                                  <CheckCircle size={10} className="mr-1" /> Bid Placed
                                </Badge>
                              )}
                            </div>
                            <div className="flex flex-wrap items-center gap-3 text-sm text-slate-500">
                              {job.location && (
                                <span className="flex items-center gap-1">
                                  <MapPin size={14} />
                                  {job.location}
                                </span>
                              )}
                              {job.start_date && (
                                <span className="flex items-center gap-1">
                                  <Calendar size={14} />
                                  {job.start_date}
                                </span>
                              )}
                              {job.required_classification && (
                                <Badge variant="outline" className="text-xs">
                                  {job.required_classification}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {!alreadyBid && (
                            <CaretRight size={20} className="text-slate-400 flex-shrink-0" />
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Panel - My Bids */}
        <div className="lg:col-span-4">
          <Card className="h-full">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Hand size={20} />
                My Bids
              </CardTitle>
              <CardDescription>Your submitted bids and outcomes</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingMyBids ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                </div>
              ) : myBids.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Hand size={48} className="mx-auto mb-4 opacity-50" />
                  <p>You have not placed any bids yet</p>
                  <p className="text-sm mt-1">Select an open job to bid</p>
                </div>
              ) : (
                <ScrollArea className="h-[450px]">
                  <div className="space-y-3 pr-4">
                    {myBids.map((bid) => (
                      <div
                        key={bid.id}
                        className="p-3 border border-slate-200 rounded-lg"
                        data-testid={`my-bid-${bid.id}`}
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-medium text-slate-900 text-sm">
                            {bid.job_title || 'Unknown Job'}
                          </h4>
                          {getOutcomeBadge(bid.outcome)}
                        </div>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <ListNumbers size={12} />
                            Rank #{bid.rank_at_bid}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock size={12} />
                            {bid.overtime_hours_at_bid?.toFixed(1)}h OT
                          </span>
                          {bid.job_location && (
                            <span className="flex items-center gap-1">
                              <MapPin size={12} />
                              {bid.job_location}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 mt-2">
                          Bid placed: {new Date(bid.bid_time).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Bid Dialog */}
      <Dialog open={bidDialogOpen} onOpenChange={setBidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Place Bid</DialogTitle>
            <DialogDescription>
              Review job details and confirm your bid
            </DialogDescription>
          </DialogHeader>

          {selectedJob && (
            <div className="space-y-4">
              {/* Job Details */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-slate-900 mb-2">{selectedJob.title}</h4>
                <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600">
                  {selectedJob.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={14} />
                      {selectedJob.location}
                    </span>
                  )}
                  {selectedJob.start_date && (
                    <span className="flex items-center gap-1">
                      <Calendar size={14} />
                      {selectedJob.start_date}
                    </span>
                  )}
                  {selectedJob.required_classification && (
                    <Badge variant="outline">{selectedJob.required_classification}</Badge>
                  )}
                </div>
              </div>

              {/* Your Bid Info */}
              <div className="p-4 border rounded-lg">
                <h5 className="font-medium text-sm text-slate-700 mb-3">Your Bid Information</h5>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Overtime Hours:</span>
                    <p className="font-bold text-lg">{eligibility?.current_overtime_hours?.toFixed(1) || '0.0'}</p>
                  </div>
                  <div>
                    <span className="text-slate-500">Current Rank:</span>
                    <p className="font-bold text-lg">#{getMyRank()}</p>
                  </div>
                </div>
              </div>

              {/* Eligibility Status */}
              {eligibility && (
                <div className={`p-4 rounded-lg ${
                  eligibility.can_bid ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    {eligibility.can_bid ? (
                      <>
                        <CheckCircle size={20} className="text-emerald-600" />
                        <span className="font-medium text-emerald-800">Eligible to Bid</span>
                      </>
                    ) : (
                      <>
                        <XCircle size={20} className="text-red-600" />
                        <span className="font-medium text-red-800">Not Eligible</span>
                      </>
                    )}
                  </div>
                  
                  {!eligibility.can_bid && eligibility.reasons?.length > 0 && (
                    <ul className="text-sm text-red-700 list-disc pl-5 space-y-1">
                      {eligibility.reasons.map((reason, idx) => (
                        <li key={idx}>{reason}</li>
                      ))}
                    </ul>
                  )}
                  
                  {eligibility.already_bid && (
                    <p className="text-sm text-amber-700 mt-2 flex items-center gap-1">
                      <Warning size={14} />
                      You have already bid on this job
                    </p>
                  )}
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setBidDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmitBid}
              disabled={!eligibility?.can_bid || submittingBid}
              data-testid="confirm-bid-btn"
            >
              {submittingBid ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Hand size={16} className="mr-2" />
                  Submit Bid
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Overtime Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Upload Overtime List</DialogTitle>
            <DialogDescription>
              Upload a CSV file to update overtime hours for bid ranking
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Template Download */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <div className="flex-1">
                <p className="font-medium text-sm">CSV Template</p>
                <p className="text-xs text-slate-500">Columns: email, overtime_hours, last_overtime_date</p>
              </div>
              <Button variant="outline" size="sm" onClick={downloadOvertimeTemplate}>
                <Download size={14} className="mr-1" />
                Download
              </Button>
            </div>

            {/* File Input */}
            <div>
              <Label htmlFor="overtime-csv">Select CSV File</Label>
              <input
                ref={fileInputRef}
                type="file"
                id="overtime-csv"
                accept=".csv"
                onChange={handleOvertimeFileSelect}
                className="mt-1 block w-full text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-medium
                  file:bg-primary file:text-white
                  hover:file:bg-primary/90
                  cursor-pointer"
                data-testid="overtime-csv-input"
              />
            </div>

            {/* Preview */}
            {uploadPreview && (
              <div className="border rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-3 py-2 text-sm font-medium flex justify-between items-center">
                  <span>Preview ({uploadPreview.totalRows} rows)</span>
                  <Badge variant="secondary">{uploadPreview.file.name}</Badge>
                </div>
                <div className="max-h-48 overflow-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {uploadPreview.headers.map(h => (
                          <TableHead key={h} className="text-xs">{h}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {uploadPreview.rows.map((row, idx) => (
                        <TableRow key={idx}>
                          {uploadPreview.headers.map(h => (
                            <TableCell key={h} className="text-xs py-1">{row[h]}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="upload-notes">Notes (optional)</Label>
              <Input
                id="upload-notes"
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                placeholder="e.g., January 2026 overtime update"
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setUploadDialogOpen(false);
              setUploadPreview(null);
              setUploadNotes('');
            }}>
              Cancel
            </Button>
            <Button 
              onClick={handleOvertimeUpload} 
              disabled={!uploadPreview || uploading}
              data-testid="confirm-overtime-upload-btn"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Upload size={16} className="mr-2" />
                  Upload & Apply
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuickCanvassBidBoard;
